/**
 * Analytics Opt-Out Persistence
 * Manages user and organization-level analytics opt-out preferences
 */

import { supabase } from "@/integrations/supabase/client";

const USER_OPT_OUT_KEY = "analytics-user-opt-out";
const ORG_OPT_OUT_KEY = "analytics-org-opt-out";

interface OptOutState {
  userOptedOut: boolean;
  orgOptedOut: boolean;
  orgId?: string;
}

// In-memory cache for fast checks
let cachedOptOutState: OptOutState = {
  userOptedOut: false,
  orgOptedOut: false,
};

/**
 * Check if analytics should be blocked based on opt-out status
 */
export function isOptedOut(): boolean {
  return cachedOptOutState.userOptedOut || cachedOptOutState.orgOptedOut;
}

/**
 * Get current opt-out state
 */
export function getOptOutState(): OptOutState {
  return { ...cachedOptOutState };
}

/**
 * Load opt-out state from localStorage and database
 */
export async function loadOptOutState(): Promise<OptOutState> {
  // Load from localStorage first (fast)
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      const userOptOut = window.localStorage.getItem(USER_OPT_OUT_KEY);
      cachedOptOutState.userOptedOut = userOptOut === "true";
      
      const orgOptOut = window.localStorage.getItem(ORG_OPT_OUT_KEY);
      if (orgOptOut) {
        const parsed = JSON.parse(orgOptOut);
        cachedOptOutState.orgOptedOut = parsed.optedOut === true;
        cachedOptOutState.orgId = parsed.orgId;
      }
    } catch (error) {
      console.warn("[Analytics] Failed to load opt-out from localStorage:", error);
    }
  }

  // Then verify against database for authenticated users
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      // Check user-level opt-out in ccpa_opt_outs table
      const { data: userOptOuts } = await supabase
        .from("ccpa_opt_outs")
        .select("opt_out_type")
        .eq("user_id", user.id)
        .in("opt_out_type", ["analytics", "sharing_opt_out"]);
      
      if (userOptOuts && userOptOuts.length > 0) {
        cachedOptOutState.userOptedOut = true;
        persistUserOptOut(true);
      }

      // Check organization-level opt-out from localStorage cache
      // (Org settings stored in notification_preferences.analytics_enabled)
      const { data: orgMembership } = await supabase
        .from("organization_members")
        .select("organization_id")
        .eq("user_id", user.id)
        .maybeSingle();
      
      if (orgMembership?.organization_id) {
        cachedOptOutState.orgId = orgMembership.organization_id;
        
        // Check org settings notification_preferences for analytics opt-out
        const { data: orgSettings } = await supabase
          .from("organization_settings")
          .select("notification_preferences")
          .eq("organization_id", orgMembership.organization_id)
          .maybeSingle();
        
        const notifPrefs = orgSettings?.notification_preferences as Record<string, unknown> | null;
        if (notifPrefs && notifPrefs.analytics_enabled === false) {
          cachedOptOutState.orgOptedOut = true;
          persistOrgOptOut(orgMembership.organization_id, true);
        }
      }
    }
  } catch (error) {
    console.warn("[Analytics] Failed to verify opt-out from database:", error);
  }

  return { ...cachedOptOutState };
}

/**
 * Set user-level analytics opt-out
 */
export async function setUserOptOut(optOut: boolean): Promise<void> {
  cachedOptOutState.userOptedOut = optOut;
  persistUserOptOut(optOut);

  // Also persist to database for authenticated users
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      if (optOut) {
        // Insert opt-out record
        await supabase.from("ccpa_opt_outs").upsert({
          user_id: user.id,
          opt_out_type: "analytics",
          opt_out_date: new Date().toISOString(),
        }, {
          onConflict: "user_id,opt_out_type"
        });
      } else {
        // Remove opt-out record
        await supabase
          .from("ccpa_opt_outs")
          .delete()
          .eq("user_id", user.id)
          .eq("opt_out_type", "analytics");
      }
    }
  } catch (error) {
    console.warn("[Analytics] Failed to persist opt-out to database:", error);
  }
}

/**
 * Set organization-level analytics opt-out (admin only)
 */
export async function setOrgOptOut(orgId: string, optOut: boolean): Promise<void> {
  cachedOptOutState.orgOptedOut = optOut;
  cachedOptOutState.orgId = orgId;
  persistOrgOptOut(orgId, optOut);

  // Persist to organization_settings notification_preferences
  try {
    // First get existing preferences
    const { data: existing } = await supabase
      .from("organization_settings")
      .select("notification_preferences")
      .eq("organization_id", orgId)
      .maybeSingle();
    
    const currentPrefs = (existing?.notification_preferences as Record<string, unknown>) || {};
    
    await supabase
      .from("organization_settings")
      .upsert({
        organization_id: orgId,
        notification_preferences: { ...currentPrefs, analytics_enabled: !optOut },
        updated_at: new Date().toISOString(),
      }, {
        onConflict: "organization_id"
      });
  } catch (error) {
    console.warn("[Analytics] Failed to persist org opt-out:", error);
  }
}

function persistUserOptOut(optOut: boolean): void {
  if (typeof window === "undefined" || !window.localStorage) return;
  
  try {
    if (optOut) {
      window.localStorage.setItem(USER_OPT_OUT_KEY, "true");
    } else {
      window.localStorage.removeItem(USER_OPT_OUT_KEY);
    }
  } catch (error) {
    console.warn("[Analytics] Failed to persist user opt-out:", error);
  }
}

function persistOrgOptOut(orgId: string, optOut: boolean): void {
  if (typeof window === "undefined" || !window.localStorage) return;
  
  try {
    if (optOut) {
      window.localStorage.setItem(ORG_OPT_OUT_KEY, JSON.stringify({ orgId, optedOut: true }));
    } else {
      window.localStorage.removeItem(ORG_OPT_OUT_KEY);
    }
  } catch (error) {
    console.warn("[Analytics] Failed to persist org opt-out:", error);
  }
}

/**
 * Clear all opt-out state (for logout)
 */
export function clearOptOutState(): void {
  cachedOptOutState = {
    userOptedOut: false,
    orgOptedOut: false,
  };
  
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      window.localStorage.removeItem(USER_OPT_OUT_KEY);
      window.localStorage.removeItem(ORG_OPT_OUT_KEY);
    } catch (error) {
      console.warn("[Analytics] Failed to clear opt-out state:", error);
    }
  }
}
